import React, {
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import RoutesMark from "components/blocks/headernav/sub/RoutesMark";
import { UsagePlan } from "pages/subscription/SubscriptionStyled";
import { formatUppercaseOnly } from "utils/functions";

import OrgStackedBarChart from "pages/dashboard/stats/graph/BarGraph";
import TableButton from "components/atoms/buttons/TableButton";
import * as MediaQuery from "components/MediaQuery";
import { IoCardOutline } from "react-icons/io5";
import { MdOutlineChangeCircle } from "react-icons/md";
import { getLimitLineAxisY } from "utils/StatsUtil";
import { buildTickValues } from "utils/ChartUtil";
import { AxisPayDayTick } from "./AxisPayDayTick";
import { BILLING_CYCLE } from "api/interfaces/subscription.interface";
import Tabs from "components/atoms/tabs/Tabs";
import { usageTypeOptions } from "utils/options";
import type { Option } from "utils/options";
import moment from "moment";
import useDropdown from "hook/useDropdown";
import useStatusMutation from "hook/useStatusMutation";
import {
  postPlanUsageChannel,
  postPlanCustomPortal,
  postPlanUsageStorage,
} from "api/planAPI";
import {
  AddPaymentMethodResponse,
  PLAN_TYPE,
  StatCardResultDto,
  UsageGraphDto,
  UsageStorageResult,
} from "api/interfaces/planInterface.interface";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import PlanSummaryCards from "./PlanSummaryCards";
import { useQuery } from "react-query";
import { COLORS } from "styles/colors";
import StorageSummary from "../storageSummary";
import { CloudModalAccount } from "pages/subscription/modal/addCloud/AddCloudModal";
import YearlyCreditsWarningBar from "../warningBar";
import SelectComponent from "components/atoms/select/SelectComponent";
import { useFlag } from "@unleash/proxy-client-react";
import { isYearlyBillingCycle } from "utils/priceUtils";
import { convertDateToFormat } from "utils/timeUtil";
import RBACWrapper from "components/blocks/function/RBACWrapper";

interface Props {
  summaryType: PLAN_TYPE;
  summary: StatCardResultDto;
  setSummaryType: React.Dispatch<SetStateAction<PLAN_TYPE>>;
  onUpgradeCycle: () => void;
  onOpenAddCloudModal: (account: CloudModalAccount) => void;
  onGraphDateChange?: (date: string) => void;
}

export default function PlanSummary(props: Props) {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { cycle, accountId, planActiveChannel, planActiveDate } =
    selectedAccount;

  const [width, setWidth] = useState(0);
  const chartRef = useRef<HTMLDivElement>(null);
  const { dropdownRef, buttonRef, isOpen, setIsOpen } = useDropdown();
  const isStorageSummary = props.summaryType === PLAN_TYPE.STORAGE;
  const isYearly = isYearlyBillingCycle(cycle);
  const enableDowngrade =
    useFlag("feature_subscription_planCycle_downgrade") || !isYearly;

  const key = "count";

  const graphRangeOptions: Option[] = useMemo(() => {
    const today = moment();
    const currentStart = today.clone().startOf("month");
    const currentEnd = today.clone().endOf("month");

    const formatRange = (start: moment.Moment, end: moment.Moment): Option => ({
      value: start.format("YYYY-MM"),
      label: `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`,
    });

    const fallback = [formatRange(currentStart, currentEnd)];

    const active = planActiveDate ? moment(planActiveDate) : null;
    if (!active || !active.isValid()) return fallback;

    const options: Option[] = [];
    const cursor = active.clone().startOf("month");

    while (cursor.isSameOrBefore(currentStart, "month")) {
      const start = cursor.clone().startOf("month");
      const end = cursor.clone().endOf("month");

      options.push(formatRange(start, end));
      cursor.add(1, "month");
    }

    return options;
  }, [planActiveDate]);

  const defaultGraphRangeOption = useMemo<Option | undefined>(() => {
    return graphRangeOptions.at(-1) ?? graphRangeOptions[0];
  }, [graphRangeOptions]);

  const [selectedGraphRangeOption, setSelectedGraphRangeOption] = useState<
    Option | undefined
  >(defaultGraphRangeOption);

  useEffect(() => {
    if (!defaultGraphRangeOption) return;
    setSelectedGraphRangeOption((prev) => {
      if (!prev) return defaultGraphRangeOption;
      const stillExists = graphRangeOptions.some((o) => o.value === prev.value);
      return stillExists ? prev : defaultGraphRangeOption;
    });
  }, [defaultGraphRangeOption, graphRangeOptions]);

  useLayoutEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    setWidth(el.clientWidth);

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? 0;
      setWidth(w);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const mutationCustomPortal = useStatusMutation(postPlanCustomPortal, {
    onSuccess: (res: AddPaymentMethodResponse) => {
      const stripeUrl = res.result;
      if (!stripeUrl) return;
      window.open(stripeUrl, "_blank", "noopener,noreferrer");
    },
    onError: (err) => {
      console.log(err);
    },
  });

  const onClickPaymentSetting = useCallback(() => {
    mutationCustomPortal.mutate({ accountId });
  }, [accountId]);

  const selectedGraphDate = selectedGraphRangeOption?.value;

  useEffect(() => {
    if (selectedGraphDate) {
      props.onGraphDateChange?.(selectedGraphDate);
    }
  }, [selectedGraphDate]);

  const { data: channelGraphData } = useQuery(
    ["postPlanUsageChannel", accountId, cycle, selectedGraphDate],
    () =>
      postPlanUsageChannel({
        accountId,
        payload: { cycle, date: selectedGraphDate! },
      }),
    {
      enabled:
        !!accountId &&
        !!selectedGraphDate &&
        props.summaryType === PLAN_TYPE.CHANNEL,
      retry: 0,
    }
  );

  const { data: storageUsageData, isFetching: isStorageFetching } = useQuery(
    ["postPlanUsageStorage", accountId, cycle, props.summaryType],
    () => postPlanUsageStorage({ accountId, payload: { cycle } }),
    {
      enabled: !!accountId && props.summaryType === PLAN_TYPE.STORAGE,
      retry: 0,
    }
  );

  const barChart = useMemo(() => {
    if (
      props.summaryType === PLAN_TYPE.CHANNEL &&
      channelGraphData?.result &&
      channelGraphData.error === 0
    ) {
      return channelGraphData.result.list.map((res: UsageGraphDto) => {
        return {
          ...res,
          date: convertDateToFormat(res.date, selectedAccount.dateFormat ?? ""),
        };
      });
    }
    return [];
  }, [channelGraphData, props.summaryType, selectedAccount.dateFormat]);

  const paymentDueDate: string | undefined = useMemo(() => {
    if (
      props.summaryType === PLAN_TYPE.CHANNEL &&
      channelGraphData?.result &&
      channelGraphData.error === 0
    ) {
      return channelGraphData.result.dueDate;
    }
    return undefined;
  }, [channelGraphData, props.summaryType]);

  const isPaymentDueThisMonth = useMemo(
    () =>
      !!paymentDueDate &&
      moment(paymentDueDate).format("YYYY-MM") === moment().format("YYYY-MM") &&
      !moment(paymentDueDate).isBefore(moment(), "day"),
    [paymentDueDate]
  );

  const paymentDueDateTick = useMemo<string | null>(() => {
    if (!isPaymentDueThisMonth || barChart.length === 0) return null;
    const dueDay = Number(moment(paymentDueDate).format("D"));
    const found = barChart.find(
      (b: UsageGraphDto) =>
        Number(moment(b.date).format("D")) === dueDay
    );
    return found ? String(found.date) : null;
  }, [barChart, isPaymentDueThisMonth, paymentDueDate]);

  const payDayDD = useMemo(() => {
    if (!paymentDueDateTick || !paymentDueDate) return null;
    return moment(paymentDueDate).format("DD");
  }, [paymentDueDateTick, paymentDueDate]);

  const todayTick = useMemo<string | null>(() => {
    if (barChart.length === 0) return null;
    const found = barChart.find(
      (b: UsageGraphDto) =>
        b.date && moment(b.date).isSame(moment(), "day")
    );
    return found ? String(found.date) : null;
  }, [barChart]);

  const axisBottomTickValues = useMemo(() => {
    if (!width || barChart.length === 0) return undefined;
    const dates = barChart.map((b: UsageGraphDto) => String(b.date));
    const computed = buildTickValues(dates, width);
    const forcedTicks = [paymentDueDateTick, todayTick].filter(
      (tick): tick is string => !!tick && !computed.includes(tick)
    );
    if (forcedTicks.length === 0) return computed;
    return [...new Set([...computed, ...forcedTicks])].sort(
      (a, b) => dates.indexOf(a) - dates.indexOf(b)
    );
  }, [barChart, width, paymentDueDateTick, todayTick]);

  const limitLineTop = useMemo(() => {
    const value = getLimitLineAxisY(planActiveChannel, barChart, key);
    if (!value) return undefined;
    return {
      value,
      label: "Channel Usage Limit",
    };
  }, [planActiveChannel, barChart]);

  const channelsGraph = [
    {
      key,
      label: "Channels",
      color: COLORS.BUTTONS2,
    },
  ];

  const storageAlloctaionData: UsageStorageResult | null = useMemo(() => {
    if (
      storageUsageData?.result &&
      Object.keys(storageUsageData.result).length > 0
    ) {
      return storageUsageData.result;
    }
    return null;
  }, [storageUsageData]);

  useLayoutEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    setWidth(el.clientWidth);

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? 0;
      setWidth(w);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [chartRef, props.summaryType]);

  return (
    <UsagePlan.SummaryWrapper>
      <UsagePlan.ManagePlanButtonWrapper>
        {isOpen && (
          <UsagePlan.ManagePlanButtonList ref={dropdownRef}>
            <button onClick={onClickPaymentSetting}>
              <IoCardOutline size={17} />
              Invoices and Payment Settings
            </button>
            {enableDowngrade && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  props.onUpgradeCycle();
                }}
              >
                <MdOutlineChangeCircle size={17} />
                Upgrade To{" "}
                {formatUppercaseOnly(
                  !isYearly ? BILLING_CYCLE.YEARLY : BILLING_CYCLE.MONTHLY
                )}{" "}
                Plan
              </button>
            )}
          </UsagePlan.ManagePlanButtonList>
        )}
        <UsagePlan.TopWrapper>
          <MediaQuery.Desktop>
            <RoutesMark />
          </MediaQuery.Desktop>
          {!isYearly && (
            <RBACWrapper requiredPermissions="manageBillingAndLicenses">
              <TableButton
                ref={buttonRef}
                label="Manage Plan"
                buttonType="primary"
                onClick={() => setIsOpen(!isOpen)}
              />
            </RBACWrapper>
          )}
        </UsagePlan.TopWrapper>
        {isYearly && (
          <>
            <YearlyCreditsWarningBar />
            <UsagePlan.TopWrapper>
              <Tabs
                tabs={usageTypeOptions}
                currentTab={props.summaryType}
                onChangeTab={(value) =>
                  props.setSummaryType(value as PLAN_TYPE)
                }
              />
              <RBACWrapper requiredPermissions="manageBillingAndLicenses">
                <TableButton
                  ref={buttonRef}
                  label="Manage Plan"
                  buttonType="primary"
                  onClick={() => setIsOpen(!isOpen)}
                />
              </RBACWrapper>
            </UsagePlan.TopWrapper>
          </>
        )}
      </UsagePlan.ManagePlanButtonWrapper>

      <PlanSummaryCards
        summary={props.summary}
        cycle={cycle}
        summaryType={props.summaryType}
        storages={storageAlloctaionData?.storages}
      />
      {isYearly && isStorageSummary ? (
        <StorageSummary
          summary={props.summary}
          cycle={cycle}
          onOpenAddCloudModal={props.onOpenAddCloudModal}
          storageAlloctaionData={storageAlloctaionData}
          isFetching={isStorageFetching}
        />
      ) : (
        <UsagePlan.GraphWrapper>
          <UsagePlan.GraphTitle>
            <SelectComponent
              options={graphRangeOptions}
              value={selectedGraphRangeOption?.value}
              onChange={(value) => {
                const option = graphRangeOptions.find((o) => o.value === value);
                if (!option) return;
                setSelectedGraphRangeOption(option);
              }}
            />
          </UsagePlan.GraphTitle>
          <UsagePlan.GraphInnterWrapper>
            <UsagePlan.GraphContent ref={chartRef}>
              <OrgStackedBarChart
                rows={barChart}
                activeOrgs={[key]}
                width={width}
                items={channelsGraph}
                groupMode={"stacked"}
                unit={"number"}
                indexBy={"date"}
                enableGridY={true}
                limitLineTop={isYearly ? limitLineTop : undefined}
                axisBottom={{
                  tickValues: axisBottomTickValues,
                  renderTick: (tick) => (
                    <AxisPayDayTick
                      x={tick.x}
                      y={tick.y}
                      value={tick.value}
                      textY={tick.textY}
                      paymentDueDateTick={paymentDueDateTick}
                      payDayDD={payDayDD}
                      todayTick={todayTick}
                    />
                  ),
                }}
              />
            </UsagePlan.GraphContent>
          </UsagePlan.GraphInnterWrapper>
        </UsagePlan.GraphWrapper>
      )}
    </UsagePlan.SummaryWrapper>
  );
}
